import { useCallback, type FC } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch } from "@ds/hooks";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { Checkbox, InputNumber } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

import { campaignItemsActions } from "../../redux/slice";

const ContentWrapper = styled.div`
  min-height: 100px;
  width: 420px;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: right;
`;

export const UpdateCampaignItemModal: FC<{
  campaignItem: CampaignItem;
  campaign: Campaign;
  allItems: CampaignItem[];
  onClose: () => void;
}> = ({ campaignItem, campaign, allItems, onClose }) => {
  const isInterleaved = campaign.mixing_strategy === "interleaved";
  const isSingleItem = allItems.length === 1;
  const maxWeight = Math.max(1, 100 - (allItems.length - 1));

  const {
    control,
    getValues,
    handleSubmit,
    formState: { dirtyFields },
  } = useForm<CampaignItem>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      ...campaignItem,
      weight: isSingleItem ? 100 : campaignItem.weight,
      settings: {
        randomize: campaignItem.settings?.randomize ?? false,
        min_gap: campaignItem.settings?.min_gap ?? 0,
        max_consecutive: campaignItem.settings?.max_consecutive ?? 0,
      },
    },
  });

  const dispatch = useRootDispatch();
  const onSubmitHandler = useCallback(() => {
    const values = getValues();
    const changedFields: Partial<CampaignItem> = {};
    if (isSingleItem && campaignItem.weight !== 100) changedFields.weight = 100;
    else if (dirtyFields.weight) changedFields.weight = values.weight;
    if (isInterleaved && dirtyFields.settings) {
      changedFields.settings = {
        randomize: values.settings?.randomize ?? false,
        min_gap: values.settings?.min_gap ?? 0,
        max_consecutive: values.settings?.max_consecutive ?? 0,
      };
    }
    if (Object.keys(changedFields).length > 0) {
      dispatch(campaignItemsActions.updateCampaignItems([campaignItem.id], changedFields));
    }
    onClose();
  }, [dispatch, campaignItem, getValues, dirtyFields, isInterleaved, isSingleItem, onClose]);

  const renderFooter = useCallback(
    () => (
      <FooterWrapper>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          onClick={onClose}
        />
        <Button
          label="Update"
          ariaLabel="update button"
          buttonType={ButtonType.Raised}
          icon={IconType.Save}
          iconPosition="right"
          isDisabled={Object.keys(dirtyFields).length === 0}
          onClick={handleSubmit(onSubmitHandler)}
        />
      </FooterWrapper>
    ),
    [onClose, handleSubmit, onSubmitHandler, dirtyFields],
  );

  return (
    <Dialog modal visible header="Edit campaign item" footer={renderFooter} onHide={onClose}>
      <ContentWrapper>
        <Controller
          name="weight"
          control={control}
          render={({ field: { ref, value, onChange }, fieldState }) => (
            <LabelWrapper label="Weight (%)" className="col-8" error={fieldState.error} isRequired>
              <InputNumber
                inputRef={ref}
                value={Number(value ?? 1)}
                min={1}
                max={maxWeight}
                suffix="%"
                isDisabled={isSingleItem}
                onChange={({ value: v }) => onChange(v ?? 1)}
              />
            </LabelWrapper>
          )}
        />

        {isInterleaved && (
          <>
            <Controller
              name="settings.randomize"
              control={control}
              render={({ field: { value, onChange } }) => (
                <LabelWrapper label="Randomize" className="col-8">
                  <Checkbox checked={!!value} onChange={e => onChange(e.checked)} />
                </LabelWrapper>
              )}
            />

            <Controller
              name="settings.min_gap"
              control={control}
              render={({ field: { ref, value, onChange }, fieldState }) => (
                <LabelWrapper label="Min Gap" className="col-8" error={fieldState.error}>
                  <InputNumber
                    inputRef={ref}
                    value={Number(value ?? 0)}
                    min={0}
                    onChange={({ value: v }) => onChange(v ?? 0)}
                  />
                </LabelWrapper>
              )}
            />

            <Controller
              name="settings.max_consecutive"
              control={control}
              render={({ field: { ref, value, onChange }, fieldState }) => (
                <LabelWrapper label="Max Consecutive" className="col-8" error={fieldState.error}>
                  <InputNumber
                    inputRef={ref}
                    value={Number(value ?? 0)}
                    min={0}
                    onChange={({ value: v }) => onChange(v ?? 0)}
                  />
                </LabelWrapper>
              )}
            />
          </>
        )}
      </ContentWrapper>
    </Dialog>
  );
};
